import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { getEditionCategoryGroupedProduct, getEditionCategoryProduct } from "src/api/editionCategory";
import { QUERY_KEYS } from "../constants";
/**
 * 상품 타입별 freeTrial 객체 생성
 */ const getMockFreeTrial = (type)=>{
    switch(type){
        case "basic":
            return undefined;
        case "freeTrial":
            return {
                eligible: true,
                reason: null,
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        case "freeTrialNotLoggedIn":
            return {
                eligible: null,
                reason: "not_logged_in",
                remainingToday: null,
                promotionId: 1,
                dailyLimit: 5
            };
        case "trialOver":
            return {
                eligible: false,
                reason: "daily_limit_exceeded",
                remainingToday: 0,
                promotionId: 1,
                dailyLimit: 5
            };
        case "owned":
            return {
                eligible: false,
                reason: "already_owned",
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        case "excluded":
            return {
                eligible: false,
                reason: "excluded_membership",
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        case "notTargetUser":
            return {
                eligible: false,
                reason: "not_target_user",
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        default:
            return undefined;
    }
};
/**
 * 상품 데이터에 mock 타입 적용
 */ const applyMockToProducts = (products, mockType, mockLicensePlan)=>{
    if (!mockType && !mockLicensePlan) return products;
    return products.map((product)=>({
            ...product,
            ...mockType && {
                freeTrial: getMockFreeTrial(mockType)
            },
            ...mockLicensePlan && {
                licensePlan: mockLicensePlan === "null" ? null : mockLicensePlan
            }
        }));
};
/**
 * 카테고리별 상품 리스트를 조회하는 훅
 * - 마지막 depth가 아닌 경우: /edition-categories/{editionId}/{categoryId} 호출
 * - 마지막 depth인 경우: /edition-categories/solvookpassen/{editionId}/grouped 호출
 */ export const useCategoryProducts = (param)=>{
    let { editionId , categoryId , offset =0 , limit =10 , sort , filters , isLastDepthByTree =false , isCategoryLoading =false  } = param;
    var _query_data, _query_data1, _groupedQuery_data, _query_data2, _groupedQuery_data1, _groupedQuery_data2, _query_data3;
    const router = useRouter();
    // Mock 쿼리파라미터 읽기
    const mockProductType = router.query.mockProductType;
    const mockLicensePlan = router.query.mockLicensePlan;
    const isEnabled = !!editionId && typeof editionId === "string" && !!categoryId && typeof categoryId === "string" && !isCategoryLoading;
    // 일반 상품 리스트 API (마지막 depth가 아닌 경우에만 호출)
    const query = useQuery({
        queryKey: [
            QUERY_KEYS.CATEGORY_PRODUCTS,
            editionId,
            categoryId,
            offset,
            limit,
            sort,
            filters
        ],
        queryFn: ()=>{
            var _filters_units, _filters_dataTypes, _filters_authors, _filters_steps;
            const request = {
                editionId: editionId,
                categoryId: categoryId,
                offset,
                limit,
                ...sort && {
                    sort: sort
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_units = filters.units) === null || _filters_units === void 0 ? void 0 : _filters_units.length) && {
                    "units[]": filters.units
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_dataTypes = filters.dataTypes) === null || _filters_dataTypes === void 0 ? void 0 : _filters_dataTypes.length) && {
                    "dataTypes[]": filters.dataTypes
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_authors = filters.authors) === null || _filters_authors === void 0 ? void 0 : _filters_authors.length) && {
                    "authors[]": filters.authors
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_steps = filters.steps) === null || _filters_steps === void 0 ? void 0 : _filters_steps.length) && {
                    "steps[]": filters.steps
                }
            };
            return getEditionCategoryProduct(request);
        },
        enabled: isEnabled && !isLastDepthByTree,
        refetchOnWindowFocus: false,
        // 필터/정렬 변경 시 이전 데이터 유지하여 깜빡임 방지
        keepPreviousData: true
    });
    // 그룹화된 상품 리스트 API (마지막 depth인 경우)
    const groupedQuery = useQuery({
        queryKey: [
            QUERY_KEYS.CATEGORY_PRODUCTS,
            "grouped",
            editionId,
            categoryId,
            filters
        ],
        queryFn: ()=>{
            var _filters_units, _filters_dataTypes, _filters_authors, _filters_steps;
            return getEditionCategoryGroupedProduct({
                editionId: editionId,
                categoryId: categoryId,
                ...(filters === null || filters === void 0 ? void 0 : (_filters_units = filters.units) === null || _filters_units === void 0 ? void 0 : _filters_units.length) && {
                    "units[]": filters.units
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_dataTypes = filters.dataTypes) === null || _filters_dataTypes === void 0 ? void 0 : _filters_dataTypes.length) && {
                    "dataTypes[]": filters.dataTypes
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_authors = filters.authors) === null || _filters_authors === void 0 ? void 0 : _filters_authors.length) && {
                    "authors[]": filters.authors
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_steps = filters.steps) === null || _filters_steps === void 0 ? void 0 : _filters_steps.length) && {
                    "steps[]": filters.steps
                }
            });
        },
        enabled: isEnabled && isLastDepthByTree,
        refetchOnWindowFocus: false,
        // 필터 변경 시 이전 데이터 유지하여 깜빡임 방지
        keepPreviousData: true
    });
    // 상품 리스트 (변환 없이 API 응답 그대로 사용)
    const rawProducts = ((_query_data = query.data) === null || _query_data === void 0 ? void 0 : _query_data.items) || [];
    // Mock 적용
    const products = applyMockToProducts(rawProducts, mockProductType, mockLicensePlan);
    // 정렬 옵션
    const sortOptions = ((_query_data1 = query.data) === null || _query_data1 === void 0 ? void 0 : _query_data1.sortItems.map((label)=>({
            value: label,
            label
        }))) || [];
    // 필터 항목 (ProductFilter용) — 마지막 depth에서는 grouped API 응답 사용
    const filterSource = isLastDepthByTree ? (_groupedQuery_data = groupedQuery.data) === null || _groupedQuery_data === void 0 ? void 0 : _groupedQuery_data.filterItems : (_query_data2 = query.data) === null || _query_data2 === void 0 ? void 0 : _query_data2.filterItems;
    const filterItems = (filterSource === null || filterSource === void 0 ? void 0 : filterSource.map((filter)=>({
            name: filter.name,
            key: filter.key,
            items: filter.items
        }))) || [];
    // 그룹화된 데이터를 AccordionGroup 형식으로 변환 (API 응답 직접 사용)
    const productGroups = ((_groupedQuery_data1 = groupedQuery.data) === null || _groupedQuery_data1 === void 0 ? void 0 : _groupedQuery_data1.groups.map((group)=>({
            id: "group-".concat(group.unit),
            title: group.unit,
            // Mock 적용
            items: applyMockToProducts(group.items, mockProductType, mockLicensePlan)
        }))) || [];
    const groupedTotalCount = ((_groupedQuery_data2 = groupedQuery.data) === null || _groupedQuery_data2 === void 0 ? void 0 : _groupedQuery_data2.totalCount) || 0;
    // 현재 활성화된 쿼리 선택
    const activeQuery = isLastDepthByTree ? groupedQuery : query;
    return {
        ...activeQuery,
        products,
        totalCount: ((_query_data3 = query.data) === null || _query_data3 === void 0 ? void 0 : _query_data3.totalCount) || 0,
        sortOptions,
        filterItems,
        productGroups,
        groupedTotalCount,
        /** 필터 로딩 상태 - 최초 로딩 시에만 true (필터 변경으로 인한 refetch 시에는 false) */ isFilterLoading: isLastDepthByTree ? groupedQuery.isLoading && !groupedQuery.data : query.isLoading && !query.data
    };
};
